import React, { ReactElement, ReactNode, useMemo, useEffect } from 'react';
import {
  GlobalConfig as GlobalConfigBase,
  ICloudinaryConfig,
  CloudinaryService,
} from '@cars4ever/public-ui';
import {
  useBrandTheme,
  BrandCode,
  initializeBrandConfig,
  BrandConfigOptions,
} from '@cars4ever/brand-config-core';

import {
  LOCALE,
  CLOUD_NAME,
  BRAND_CONFIG_URL,
} from '@digital-dealer/environments';
import { logger } from '@digital-dealer/utils';
import {
  ModalProvider,
  StoreProvider,
  ErrorBoundary,
  WidgetLayoutProvider,
} from '@digital-dealer/components';

import styles from './styles.module.scss';

export interface IGlobalConfigProps {
  children: ReactNode;
}

const brandConfigInitOptions: BrandConfigOptions = {
  apiBaseUrl: BRAND_CONFIG_URL,
};

const handleCloudinaryImageError = (src: string): void => {
  logger.trackWarning(`Image (${src}) is not found`, {
    catcher: 'OptimizedImage.error',
  });
};

const cloudinaryConfig: ICloudinaryConfig = {
  cloudName: CLOUD_NAME,
  handleError: handleCloudinaryImageError,
};

// Eagerly initialize at module load — MediaUtilsProvider only inits in useEffect,
// which can run after React 19 renders descendant components that call
// CloudinaryService.getInstance() (e.g. inside lazy-loaded modals like StoreSelector).
CloudinaryService.init(cloudinaryConfig);

const GlobalConfig = ({ children }: IGlobalConfigProps): ReactElement => {
  const theme = useMemo(
    (): string => styles[window.xprops.theme] || window.xprops.theme,
    [],
  );

  useEffect((): void => {
    void initializeBrandConfig(brandConfigInitOptions);
  }, []);

  useBrandTheme(theme as BrandCode);

  return (
    <GlobalConfigBase
      locale={LOCALE}
      cloudinaryConfig={cloudinaryConfig}
    >
      <WidgetLayoutProvider>
        <ErrorBoundary>
          <ModalProvider>
            <StoreProvider>{children}</StoreProvider>
          </ModalProvider>
        </ErrorBoundary>
      </WidgetLayoutProvider>
    </GlobalConfigBase>
  );
};

export default GlobalConfig;
